import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  LogOut,
  MapPin,
  Calendar,
  Clock,
  Users,
  Eye,
  Pencil,
  Trash2,
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  Loader2,
  GraduationCap,
  UserPlus,
  Lock,
  Unlock,
  KeyRound,
  RefreshCw,
  Signature,
  BookOpenCheck,
  DoorOpen,
  DoorClosed,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { adminApi, clearSession, getUsername } from "@/lib/auth";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import AttendanceEditDialog from "@/components/AttendanceEditDialog";
import SignaturePad from "@/components/SignaturePad";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import InstructorSignatureOnboarding from "@/components/InstructorSignatureOnboarding";
import { InstructorCourseList } from "@/components/InstructorCourseList";
import InstructorMasksPanel from "@/components/InstructorMasksPanel";
import { MasksDecisionPanel } from "@/components/MasksDecisionPanel";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function InstructorScreen() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [current, setCurrent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [exams, setExams] = useState([]);
  const [tabsLoading, setTabsLoading] = useState(false);
  // ACCESO A EXAMEN: estado del lock + contadores (asistentes / entregados /
  // aprobados). Se refresca cada vez que cambia el curso seleccionado.
  const [examStatus, setExamStatus] = useState(null);
  const [examToggling, setExamToggling] = useState(false);
  // "Añadir alumno" — alta manual desde el panel del instructor.
  const [addAttOpen, setAddAttOpen] = useState(false);
  const [addAttSaving, setAddAttSaving] = useState(false);
  const [addAttForm, setAddAttForm] = useState({
    name: "", surname: "", dni: "", profession: "",
  });
  const [askSignatureFor, setAskSignatureFor] = useState(null);
  // (Eliminado: el instructor ya NO firma su asistencia al curso. Sólo se
  // conserva la firma maestra, que se gestiona desde el panel del gestor.)
  const [viewSignature, setViewSignature] = useState(null);
  const [editAttendance, setEditAttendance] = useState(null);
  const [deleteAttendance, setDeleteAttendance] = useState(null);
  // Captura de firma DESDE el panel del instructor (alumno firma en su movil)
  // signTarget = { attendance, session: 1 | 2 } | null
  const [signTarget, setSignTarget] = useState(null);
  const [signSaving, setSignSaving] = useState(false);
  // Borrado de una firma concreta (sin borrar la asistencia)
  const [clearSignatureTarget, setClearSignatureTarget] = useState(null);

  // Helper: id del curso efectivo (curso seleccionado en la lista o el de hoy)
  const _activeCid = () => {
    const c = courses.find((c) => c.id === selectedCourseId) || current;
    return c ? c.id : null;
  };

  const handleInstructorEdit = async (id, payload) => {
    const cid = _activeCid();
    if (!cid) throw new Error("No hay curso seleccionado");
    const res = await adminApi.put(
      `/instructor/courses/${cid}/attendances/${id}`,
      payload
    );
    setAttendances((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...res.data } : a))
    );
  };

  const handleInstructorDelete = async () => {
    const cid = _activeCid();
    if (!cid || !deleteAttendance) return;
    try {
      await adminApi.delete(
        `/instructor/courses/${cid}/attendances/${deleteAttendance.id}`
      );
      setAttendances((prev) =>
        prev.filter((a) => a.id !== deleteAttendance.id)
      );
      toast.success("Asistencia eliminada.");
      setDeleteAttendance(null);
    } catch (e) {
      logError("No se pudo eliminar la asistencia", e);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  // Guarda la firma capturada para una asistencia + sesion concreta.
  const handleSaveRowSignature = async (dataUrl) => {
    const cid = _activeCid();
    if (!cid || !signTarget?.attendance) return;
    setSignSaving(true);
    try {
      const res = await adminApi.post(
        `/instructor/courses/${cid}/attendances/${signTarget.attendance.id}/sign`,
        { session: signTarget.session, signature_base64: dataUrl }
      );
      setAttendances((prev) =>
        prev.map((a) => (a.id === signTarget.attendance.id ? { ...a, ...res.data } : a))
      );
      toast.success(
        signTarget.session >= 2
          ? `Firma de la ${signTarget.session}ª sesión guardada.`
          : "Firma guardada."
      );
      setSignTarget(null);
    } catch (e) {
      logError("No se pudo guardar la firma", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar la firma.");
    } finally {
      setSignSaving(false);
    }
  };

  // Borra una firma concreta (sesion 1 o 2) sin tocar la asistencia.
  const handleClearStudentSignature = async () => {
    const cid = _activeCid();
    if (!cid || !clearSignatureTarget) return;
    const { attendance, session } = clearSignatureTarget;
    try {
      await adminApi.delete(
        `/instructor/courses/${cid}/attendances/${attendance.id}/signature`,
        { params: { session } }
      );
      const field = session >= 2
        ? `signature_session${session}_base64`
        : "signature_base64";
      setAttendances((prev) =>
        prev.map((a) => (a.id === attendance.id ? { ...a, [field]: "" } : a))
      );
      toast.success("Firma eliminada.");
      setClearSignatureTarget(null);
      setViewSignature(null);
    } catch (e) {
      logError("No se pudo eliminar la firma", e);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la firma.");
    }
  };
  const [needsProfileSignature, setNeedsProfileSignature] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [me, cur, mine, sig] = await Promise.all([
        adminApi.get("/auth/me-instructor"),
        adminApi.get("/instructor/current-course"),
        adminApi.get("/instructor/courses"),
        adminApi.get("/instructor/me/signature"),
      ]);
      // Si el instructor entró con una contraseña temporal, obligamos a
      // cambiarla antes de ver nada.
      if (me.data?.must_change_password) {
        navigate("/instructor/cambiar-contrasena", { replace: true });
        return;
      }
      setProfile(me.data);
      setCurrent(cur.data || null);
      setCourses(mine.data || []);
      // Show onboarding if no master signature is stored yet.
      setNeedsProfileSignature(!sig.data?.has_signature);
    } catch (e) {
      logError("No se pudo cargar la información del instructor", e);
      toast.error("No se pudo cargar la información del instructor.");
    } finally {
      setLoading(false);
    }
    // adminApi/logError/toast son imports estables; setters de useState
    // tambien lo son.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // When we have a current or a selected course, load attendance/exam data
  useEffect(() => {
    const cid = selectedCourseId || current?.id;
    if (!cid) {
      setAttendances([]);
      setExams([]);
      setExamStatus(null);
      return;
    }
    // Guard contra race: si el usuario cambia de curso antes de que
    // termine la request anterior, evitamos que la respuesta antigua
    // sobreescriba el estado.
    let cancelled = false;
    setTabsLoading(true);
    Promise.all([
      adminApi.get(`/instructor/courses/${cid}/attendances`),
      adminApi.get(`/instructor/courses/${cid}/exams`),
      adminApi.get(`/instructor/courses/${cid}/exam-status`),
    ])
      .then(([a, e, s]) => {
        if (cancelled) return;
        setAttendances(a.data || []);
        setExams(e.data || []);
        setExamStatus(s.data || null);
      })
      .catch(() => {
        if (!cancelled) toast.error("Error cargando datos del curso.");
      })
      .finally(() => {
        if (!cancelled) setTabsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // adminApi/toast son imports estables; setters de useState lo son
    // tambien. Solo necesitamos reactivar el effect cuando cambian las
    // dependencias reales (curso seleccionado o curso actual).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId, current]);

  // Refresca SOLO el panel de "Acceso a examen" (contadores + lock state).
  const refreshExamStatus = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const r = await adminApi.get(`/instructor/courses/${cid}/exam-status`);
      setExamStatus(r.data || null);
    } catch (e) {
      logError("No se pudo refrescar el estado del examen", e);
    }
  }, []);

  // Toggle del acceso al examen (instructor).
  const handleToggleExamAccess = useCallback(async (cid, newValue) => {
    if (!cid) return;
    setExamToggling(true);
    try {
      const r = await adminApi.post(
        `/instructor/courses/${cid}/exam-toggle`,
        { unlocked: newValue },
      );
      setExamStatus((prev) =>
        prev ? { ...prev, exam_unlocked: !!r.data?.exam_unlocked } : prev,
      );
      toast.success(
        newValue
          ? "Examen INICIADO. Los alumnos ya pueden entrar."
          : "Examen FINALIZADO.",
      );
    } catch (e) {
      logError("No se pudo cambiar el acceso al examen", e);
      const detail = e.response?.data?.detail || "No se pudo cambiar el acceso al examen.";
      toast.error(typeof detail === "string" ? detail : "Error inesperado.");
    } finally {
      setExamToggling(false);
    }
  }, []);

  // Toggle apertura/cierre de la asistencia (firma) del curso.
  const [attendanceToggling, setAttendanceToggling] = useState(false);
  const handleToggleAttendance = useCallback(async (cid, newValue) => {
    if (!cid) return;
    setAttendanceToggling(true);
    try {
      const r = await adminApi.post(
        `/instructor/courses/${cid}/attendance-toggle`,
        { open: newValue },
      );
      setExamStatus((prev) =>
        prev ? { ...prev, attendance_open: !!r.data?.attendance_open } : prev,
      );
      toast.success(
        newValue
          ? "Asistencia ABIERTA. Los alumnos ya pueden firmar."
          : "Asistencia CERRADA.",
      );
    } catch (e) {
      logError("No se pudo cambiar la asistencia", e);
      toast.error(e.response?.data?.detail || "Error inesperado.");
    } finally {
      setAttendanceToggling(false);
    }
  }, []);

  // Corrector del examen: preguntas con la respuesta correcta marcada.
  const [correctorOpen, setCorrectorOpen] = useState(false);
  const [correctorQuestions, setCorrectorQuestions] = useState([]);
  const [correctorLoading, setCorrectorLoading] = useState(false);
  const openCorrector = useCallback(async (cid) => {
    if (!cid) return;
    setCorrectorOpen(true);
    setCorrectorLoading(true);
    try {
      const r = await adminApi.get(
        `/instructor/courses/${cid}/exam-questions`
      );
      setCorrectorQuestions(r.data?.questions || []);
    } catch (e) {
      logError("No se pudieron cargar las preguntas del examen", e);
      toast.error("No se pudieron cargar las preguntas.");
      setCorrectorOpen(false);
    } finally {
      setCorrectorLoading(false);
    }
  }, []);

  const handleLogout = () => {
    clearSession();
    toast.success("Sesión cerrada.");
    navigate("/instructor/login");
  };

  // (Handler `handleSign` eliminado junto al botón "Firmar mi asistencia".)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex items-center gap-3 text-zinc-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          Cargando…
        </div>
      </div>
    );
  }

  const activeCourse =
    courses.find((c) => c.id === selectedCourseId) || current || null;

  const handleMasksDecided = (courseId, value) => {
    const patch = (c) => (c && c.id === courseId ? { ...c, masks_decision: value } : c);
    setCourses((list) => list.map(patch));
    setCurrent((c) => patch(c));
  };

  // ===== Añadir asistencia (Instructor) =====
  const handleAddAttSubmit = async () => {
    const f = addAttForm;
    if (!activeCourse) {
      toast.error("Selecciona primero un curso.");
      return;
    }
    if (!f.name.trim() || !f.surname.trim() || !f.dni.trim()) {
      toast.error("Nombre, apellidos y DNI son obligatorios.");
      return;
    }
    setAddAttSaving(true);
    try {
      const res = await adminApi.post(
        `/instructor/courses/${activeCourse.id}/attendances`,
        {
          course_id: activeCourse.id,
          course_name: activeCourse.name || activeCourse.client_name || "",
          name: f.name.trim().toUpperCase(),
          surname: f.surname.trim().toUpperCase(),
          dni: f.dni.trim().toUpperCase(),
          profession: f.profession.trim().toUpperCase(),
          signature_base64: "",
          consent_accepted: true,
          image_consent_denied: false,
        }
      );
      setAttendances((prev) => [res.data, ...prev]);
      toast.success("Alumno añadido.");
      setAddAttOpen(false);
      // Inmediatamente abrimos el SignaturePad para registrar su firma.
      setAskSignatureFor(res.data);
      setAddAttForm({ name: "", surname: "", dni: "", profession: "" });
    } catch (e) {
      logError("No se pudo añadir el alumno", e);
      toast.error(e?.response?.data?.detail || "No se pudo añadir.");
    } finally {
      setAddAttSaving(false);
    }
  };

  const handleSaveStudentSignature = async (signature_base64) => {
    if (!askSignatureFor) return;
    try {
      const res = await adminApi.put(
        `/attendance/${askSignatureFor.id}`,
        { signature_base64 }
      );
      setAttendances((prev) =>
        prev.map((a) => (a.id === askSignatureFor.id ? res.data : a))
      );
      toast.success("Firma registrada.");
    } catch (e) {
      logError("No se pudo guardar la firma", e);
      toast.error(e?.response?.data?.detail || "Error al guardar la firma.");
    } finally {
      setAskSignatureFor(null);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA]" data-testid="instructor-screen">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
            data-testid="instructor-home-button"
          >
            <ChevronLeft className="h-4 w-4" />
            Inicio
          </button>
          <div className="flex items-center gap-4">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 hidden sm:flex items-center gap-2">
              <GraduationCap className="h-3.5 w-3.5" />
              Instructor
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/instructor/cambiar-contrasena")}
              className="h-9 border border-zinc-300 hover:bg-zinc-50"
              data-testid="instructor-change-password-button"
              title="Cambiar contraseña"
            >
              <KeyRound className="h-3.5 w-3.5 mr-1.5" />
              <span className="hidden sm:inline">Contraseña</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleLogout}
              className="h-9 border border-zinc-300 hover:bg-zinc-50"
              data-testid="instructor-logout-button"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Instructor identity */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
            <span className="inline-block h-2 w-2 bg-black" />
            Sesión iniciada
          </div>
          <h1
            className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]"
            data-testid="instructor-fullname"
          >
            {profile?.name} {profile?.surname}
          </h1>
          <p className="mt-2 text-zinc-500 text-sm font-mono">{profile?.email}</p>
        </div>

        {/* Current / selected course panel */}
        {activeCourse ? (
          <div
            className="bg-zinc-950 text-white border border-zinc-800 rounded-lg p-6 md:p-8 mb-10"
            data-testid="instructor-current-course"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-[0.18em] text-zinc-400 mb-2">
                  {current && activeCourse.id === current.id
                    ? "Curso de hoy"
                    : "Curso seleccionado"}
                </div>
                <h2 className="heading-font text-2xl md:text-3xl font-extrabold mb-3">
                  {activeCourse.client_name || activeCourse.name}
                </h2>
                {activeCourse.access_pin && (
                  <div
                    className="mb-4 inline-flex items-center gap-3 px-4 py-3 rounded-md bg-white text-zinc-950 shadow-lg"
                    data-testid="instructor-course-pin"
                  >
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                      PIN para los alumnos
                    </span>
                    <span className="font-mono text-3xl tracking-[0.4em] font-extrabold">
                      {activeCourse.access_pin}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1.5 gap-x-6 text-sm text-zinc-200">
                  <div className="inline-flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
                    <div>
                      <div>{activeCourse.address || "—"}</div>
                      {activeCourse.city && (
                        <div className="text-zinc-400">
                          {activeCourse.city}
                          {activeCourse.province ? `, ${activeCourse.province}` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-zinc-400" />
                    {formatDate(activeCourse.date1)}
                    {activeCourse.days >= 2 && activeCourse.date2
                      ? ` + ${formatDate(activeCourse.date2)}`
                      : ""}
                    {activeCourse.days >= 3 && activeCourse.date3
                      ? ` + ${formatDate(activeCourse.date3)}`
                      : ""}
                  </div>
                  {(activeCourse.time_start_day1 ||
                    activeCourse.time_start_day2) && (
                    <div className="inline-flex items-center gap-2">
                      <Clock className="h-4 w-4 text-zinc-400" />
                      {activeCourse.time_start_day1 && activeCourse.time_end_day1
                        ? `${activeCourse.time_start_day1}–${activeCourse.time_end_day1}`
                        : ""}
                      {activeCourse.time_start_day2 &&
                      activeCourse.time_end_day2
                        ? ` · ${activeCourse.time_start_day2}–${activeCourse.time_end_day2}`
                        : ""}
                      {activeCourse.days >= 3 &&
                      activeCourse.time_start_day3 &&
                      activeCourse.time_end_day3
                        ? ` · ${activeCourse.time_start_day3}–${activeCourse.time_end_day3}`
                        : ""}
                    </div>
                  )}
                  <div className="inline-flex items-center gap-2 uppercase font-semibold tracking-wider text-xs">
                    {activeCourse.region_name || "—"}
                    {activeCourse.course_type && (
                      <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[10px]">
                        {{ inicial: "INICIAL", reciclaje: "RECICLAJE", ppaa: "PPAA" }[activeCourse.course_type] || "RECICLAJE"}
                      </span>
                    )}
                    {activeCourse.hours != null && (
                      <span className="text-zinc-400 normal-case font-normal tracking-normal">
                        · {activeCourse.hours} h
                      </span>
                    )}
                  </div>

                  {/* Persona de contacto */}
                  {(activeCourse.contact_name ||
                    activeCourse.contact_phone ||
                    activeCourse.contact_email) && (
                    <div className="md:col-span-2 pt-2 mt-1 border-t border-zinc-800/60">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                        Persona de contacto
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        {activeCourse.contact_name && (
                          <span>{activeCourse.contact_name}</span>
                        )}
                        {activeCourse.contact_phone && (
                          <a
                            href={`tel:${activeCourse.contact_phone}`}
                            className="text-zinc-300 hover:text-white underline-offset-2 hover:underline"
                          >
                            {activeCourse.contact_phone}
                          </a>
                        )}
                        {activeCourse.contact_email && (
                          <a
                            href={`mailto:${activeCourse.contact_email}`}
                            className="text-zinc-300 hover:text-white underline-offset-2 hover:underline"
                          >
                            {activeCourse.contact_email}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Flags + DESA + máx. alumnos */}
                  <div className="md:col-span-2 flex flex-wrap gap-2 pt-2 mt-1 border-t border-zinc-800/60">
                    {activeCourse.fundae && (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-amber-500/20 text-amber-200 border border-amber-500/30">
                        FUNDAE
                      </span>
                    )}
                    {activeCourse.defibrillator && (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                        DESA · {activeCourse.defibrillator}
                      </span>
                    )}
                    {activeCourse.max_students != null && (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                        Máx. {activeCourse.max_students} alumnos
                      </span>
                    )}
                    {activeCourse.exam_question_count != null && activeCourse.course_type !== "ppaa" && (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                        Examen · {activeCourse.exam_question_count} preguntas
                      </span>
                    )}
                  </div>

                  {/* Descripción */}
                  {activeCourse.description && (
                    <div className="md:col-span-2 mt-1 text-zinc-300 italic">
                      “{activeCourse.description}”
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-dashed border-zinc-300 rounded-lg p-8 text-center mb-10">
            <h2 className="heading-font text-xl font-bold text-zinc-950">
              No tienes ningún curso programado para hoy
            </h2>
            <p className="text-sm text-zinc-600 mt-2">
              Selecciona uno de tus cursos asignados abajo para consultar
              detalles.
            </p>
          </div>
        )}

        {/* ===== Controles del curso: Asistencia + Examen + Corrector ===== */}
        {activeCourse && (
          <section
            className="rounded-lg border-2 border-zinc-200 bg-white p-5 md:p-6 mb-8"
            data-testid="instructor-course-controls-panel"
          >
            {/* Fila 1: Asistencia */}
            <div
              className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-md border-2 p-4 transition-colors ${
                examStatus?.attendance_open
                  ? "bg-emerald-50 border-emerald-500"
                  : "bg-zinc-50 border-zinc-300"
              }`}
              data-testid="instructor-attendance-panel"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1 inline-flex items-center gap-2">
                  <Signature className="h-3.5 w-3.5" />
                  Asistencia
                </div>
                <h3 className="heading-font text-lg font-extrabold text-zinc-950">
                  {examStatus?.attendance_open
                    ? "La asistencia está ABIERTA"
                    : "La asistencia está CERRADA"}
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Cuando esté abierta, cualquier alumno con el PIN puede
                  firmar. Cuando esté cerrada, nadie puede firmar.
                </p>
              </div>
              {examStatus?.attendance_open ? (
                <Button
                  type="button"
                  onClick={() => handleToggleAttendance(activeCourse.id, false)}
                  disabled={attendanceToggling}
                  className="h-12 px-5 bg-rose-600 hover:bg-rose-700 text-white rounded-md font-bold tracking-wide shrink-0"
                  data-testid="attendance-close-btn"
                >
                  {attendanceToggling ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <DoorClosed className="h-4 w-4 mr-2" />
                  )}
                  CERRAR ASISTENCIA
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => handleToggleAttendance(activeCourse.id, true)}
                  disabled={attendanceToggling}
                  className="h-12 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-bold tracking-wide shrink-0"
                  data-testid="attendance-open-btn"
                >
                  {attendanceToggling ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <DoorOpen className="h-4 w-4 mr-2" />
                  )}
                  ABRIR ASISTENCIA
                </Button>
              )}
            </div>

            {/* Fila 2: Examen (los cursos PPAA no tienen examen) */}
            {activeCourse.course_type !== "ppaa" && (
            <div
              className={`mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-md border-2 p-4 transition-colors ${
                examStatus?.exam_unlocked
                  ? "bg-emerald-50 border-emerald-500"
                  : "bg-zinc-50 border-zinc-300"
              }`}
              data-testid="instructor-exam-access-panel"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1 inline-flex items-center gap-2">
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  Examen
                </div>
                <h3 className="heading-font text-lg font-extrabold text-zinc-950">
                  {examStatus?.exam_unlocked
                    ? "El examen está ABIERTO"
                    : "El examen está CERRADO"}
                </h3>
                {examStatus && (
                  <div className="mt-2 grid grid-cols-3 gap-4 max-w-md">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Asistentes</div>
                      <div
                        className="font-mono text-lg font-bold text-zinc-900"
                        data-testid="exam-counter-attendees"
                      >
                        {examStatus.total_attendees}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Entregados</div>
                      <div
                        className="font-mono text-lg font-bold text-zinc-900"
                        data-testid="exam-counter-submitted"
                      >
                        {examStatus.submitted_unique}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                        Aprobados ({examStatus.pass_score}%)
                      </div>
                      <div
                        className="font-mono text-lg font-bold text-emerald-700"
                        data-testid="exam-counter-passed"
                      >
                        {examStatus.passed_unique}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openCorrector(activeCourse.id)}
                  className="h-10 px-3 border-zinc-300 hover:bg-zinc-50"
                  data-testid="exam-corrector-btn"
                  title="Ver todas las preguntas con la respuesta correcta marcada"
                >
                  <BookOpenCheck className="h-4 w-4 mr-2" />
                  Ver corrector
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => refreshExamStatus(activeCourse.id)}
                  disabled={tabsLoading || examToggling}
                  className="h-10 px-3 text-zinc-600 hover:bg-zinc-100"
                  data-testid="exam-refresh-btn"
                  title="Refrescar contadores"
                  aria-label="Refrescar contadores"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                {examStatus?.exam_unlocked ? (
                  <Button
                    type="button"
                    onClick={() => handleToggleExamAccess(activeCourse.id, false)}
                    disabled={examToggling}
                    className="h-12 px-5 bg-rose-600 hover:bg-rose-700 text-white rounded-md font-bold tracking-wide"
                    data-testid="exam-lock-btn"
                  >
                    {examToggling ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Lock className="h-4 w-4 mr-2" />
                    )}
                    FIN EXAMEN
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={() => handleToggleExamAccess(activeCourse.id, true)}
                    disabled={examToggling}
                    className="h-12 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-bold tracking-wide"
                    data-testid="exam-unlock-btn"
                  >
                    {examToggling ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Unlock className="h-4 w-4 mr-2" />
                    )}
                    INICIO EXAMEN
                  </Button>
                )}
              </div>
            </div>
            )}

            {/* Fila 3: Mascarillas (Reciclaje / PPAA) */}
            <MasksDecisionPanel course={activeCourse} onDecided={handleMasksDecided} />
          </section>
        )}

        {/* Mascarillas RCP: confirmación de recepción de envíos */}
        <InstructorMasksPanel />

        {/* My courses list + tabs for selected course */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Course list */}
          <aside className="md:col-span-1">
            <h3 className="heading-font text-lg font-bold text-zinc-950 mb-3">
              Mis cursos asignados
            </h3>
            <InstructorCourseList
              courses={courses}
              activeId={selectedCourseId || current?.id}
              onSelect={setSelectedCourseId}
              formatDate={formatDate}
            />
          </aside>

          {/* Tabs */}
          <section className="md:col-span-2">
            {!activeCourse ? (
              <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-sm text-zinc-500">
                Selecciona un curso para ver asistencias y exámenes.
              </div>
            ) : (
              <Tabs defaultValue="attendances" className="w-full">
                <TabsList className="bg-white border border-zinc-200 rounded-md p-1 h-auto">
                  <TabsTrigger
                    value="attendances"
                    className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
                    data-testid="instructor-tab-attendances"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Asistencias ({attendances.length})
                  </TabsTrigger>
                  {activeCourse.course_type !== "ppaa" && (
                  <TabsTrigger
                    value="exams"
                    className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
                    data-testid="instructor-tab-exams"
                  >
                    <ClipboardCheck className="h-4 w-4 mr-2" />
                    Exámenes ({exams.length})
                  </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="attendances" className="mt-4">
                  <div className="flex justify-end mb-3">
                    <Button
                      onClick={() => setAddAttOpen(true)}
                      className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
                      data-testid="instructor-attendance-add-button"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Añadir alumno
                    </Button>
                  </div>
                  {tabsLoading ? (
                    <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-zinc-500 text-sm">
                      Cargando…
                    </div>
                  ) : attendances.length === 0 ? (
                    <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-sm text-zinc-500">
                      Aún no hay asistencias firmadas para este curso.
                    </div>
                  ) : (
                    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                      {attendances.map((a) => (
                        <div
                          key={a.id}
                          className="px-5 py-3 border-b border-zinc-100 last:border-b-0 flex items-center justify-between gap-3"
                          data-testid={`instructor-attendance-${a.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-zinc-950 text-sm truncate">
                              {a.name} {a.surname}
                            </div>
                            <div className="mt-0.5 text-xs text-zinc-600 inline-flex items-center gap-3">
                              <span className="font-mono">{a.dni}</span>
                              <span>{formatDateTime(a.created_at)}</span>
                              {a.image_consent_denied && (
                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded">
                                  NO IMAGEN
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditAttendance(a)}
                              className="h-9 border border-zinc-300 hover:bg-zinc-50"
                              data-testid={`instructor-edit-attendance-${a.id}`}
                              title="Editar datos administrativos"
                              aria-label="Editar datos del alumno"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            {/* Boton Firma — abre el visor o, si no hay
                                firma, captura directamente */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (a.signature_base64) {
                                  setViewSignature(a);
                                } else {
                                  setSignTarget({ attendance: a, session: 1 });
                                }
                              }}
                              className="h-9 border border-zinc-300 hover:bg-zinc-50"
                              data-testid={`view-attendance-signature-${a.id}`}
                            >
                              <Eye className="h-3.5 w-3.5 mr-1.5" />
                              Firma
                            </Button>
                            {/* Firmar en el movil del instructor (sesion 1 o 2 segun aplique) */}
                            {(() => {
                              const isInicial = ((activeCourse?.course_type) || "").toLowerCase() === "inicial";
                              const courseDays = parseInt(activeCourse?.days, 10) || 1;
                              const needsSig1 = !a.signature_base64;
                              const needsSig2 = isInicial
                                && courseDays >= 2
                                && !a.signature_session2_base64;
                              const needsSig3 = isInicial
                                && courseDays >= 3
                                && !a.signature_session3_base64;
                              if (needsSig1) {
                                return (
                                  <Button
                                    size="sm"
                                    onClick={() => setSignTarget({ attendance: a, session: 1 })}
                                    className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid={`instructor-sign1-attendance-${a.id}`}
                                    title="Capturar firma del alumno en este móvil"
                                  >
                                    <Signature className="h-3.5 w-3.5 mr-1.5" />
                                    Firmar
                                  </Button>
                                );
                              }
                              if (needsSig2) {
                                return (
                                  <Button
                                    size="sm"
                                    onClick={() => setSignTarget({ attendance: a, session: 2 })}
                                    className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid={`instructor-sign2-attendance-${a.id}`}
                                    title="Capturar firma de la 2ª sesión"
                                  >
                                    <Signature className="h-3.5 w-3.5 mr-1.5" />
                                    Firma 2ª sesión
                                  </Button>
                                );
                              }
                              if (needsSig3) {
                                return (
                                  <Button
                                    size="sm"
                                    onClick={() => setSignTarget({ attendance: a, session: 3 })}
                                    className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid={`instructor-sign3-attendance-${a.id}`}
                                    title="Capturar firma de la 3ª sesión"
                                  >
                                    <Signature className="h-3.5 w-3.5 mr-1.5" />
                                    Firma 3ª sesión
                                  </Button>
                                );
                              }
                              return null;
                            })()}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteAttendance(a)}
                              className="h-9 px-2 border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                              data-testid={`instructor-delete-attendance-${a.id}`}
                              title="Eliminar asistencia"
                              aria-label="Eliminar asistencia"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="exams" className="mt-4">
                  {tabsLoading ? (
                    <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-zinc-500 text-sm">
                      Cargando…
                    </div>
                  ) : exams.length === 0 ? (
                    <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-sm text-zinc-500">
                      Aún no se ha presentado ningún examen.
                    </div>
                  ) : (
                    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                      {exams.map((e) => (
                        <div
                          key={e.id}
                          className="px-5 py-3 border-b border-zinc-100 last:border-b-0 flex items-center justify-between gap-3"
                          data-testid={`instructor-exam-${e.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-zinc-950 text-sm truncate">
                              {e.name} {e.surname}
                            </div>
                            <div className="mt-0.5 text-xs text-zinc-600 inline-flex items-center gap-3">
                              <span className="font-mono">{e.dni}</span>
                              <span>{formatDateTime(e.created_at)}</span>
                            </div>
                          </div>
                          <div className="shrink-0 flex items-center gap-3">
                            <div className="text-right">
                              <div className="font-bold text-zinc-950 font-mono">
                                {e.correct} / {e.total}
                              </div>
                              <div className="text-[11px] text-zinc-500">
                                mín. {e.pass_score}%
                              </div>
                            </div>
                            {e.passed ? (
                              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                            ) : (
                              <XCircle className="h-6 w-6 text-red-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </section>
        </div>
      </main>

      <Dialog open={!!viewSignature} onOpenChange={(o) => !o && setViewSignature(null)}>
        <DialogContent
          className="sm:max-w-2xl"
          data-testid="attendance-signature-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              Firma de asistencia
            </DialogTitle>
            <DialogDescription>
              {viewSignature ? (
                <>
                  {viewSignature.name} {viewSignature.surname} · DNI{" "}
                  {viewSignature.dni}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {viewSignature && (
            <div className="bg-white border-2 border-zinc-200 rounded-md p-3">
              <img
                src={viewSignature.signature_base64}
                alt="Firma del alumno"
                className="w-full h-auto max-h-[60vh] object-contain"
              />
            </div>
          )}
          {viewSignature && (
            <DialogFooter className="gap-2 mt-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSignTarget({ attendance: viewSignature, session: 1 });
                  setViewSignature(null);
                }}
                className="border border-zinc-300"
                data-testid="instructor-resign-signature"
              >
                <Signature className="h-4 w-4 mr-2" />
                Re-firmar
              </Button>
              <Button
                onClick={() =>
                  setClearSignatureTarget({ attendance: viewSignature, session: 1 })
                }
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="instructor-clear-signature-button"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar firma
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <AttendanceEditDialog
        attendance={editAttendance}
        open={!!editAttendance}
        onClose={() => setEditAttendance(null)}
        onSubmit={handleInstructorEdit}
      />

      <AlertDialog
        open={!!deleteAttendance}
        onOpenChange={(o) => !o && setDeleteAttendance(null)}
      >
        <AlertDialogContent data-testid="instructor-delete-attendance-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar asistencia?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                Se eliminará la asistencia firmada de{" "}
                <span className="font-semibold text-zinc-950">
                  {deleteAttendance?.name} {deleteAttendance?.surname}
                </span>{" "}
                (DNI {deleteAttendance?.dni}). Esta acción no se puede
                deshacer.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="instructor-delete-attendance-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleInstructorDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="instructor-delete-attendance-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InstructorSignatureOnboarding
        open={needsProfileSignature}
        onSaved={() => setNeedsProfileSignature(false)}
      />

      {/* "Añadir alumno" dialog para Instructor */}
      <Dialog open={addAttOpen} onOpenChange={setAddAttOpen}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="instructor-attendance-add-dialog"
        >
          <DialogHeader>
            <DialogTitle>Añadir alumno</DialogTitle>
            <DialogDescription>
              {activeCourse
                ? `Curso: ${activeCourse.name || activeCourse.client_name}`
                : "Selecciona primero un curso."}
              <br />Tras añadir el alumno se abrirá la pantalla de firma.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700">Nombre *</label>
                <Input
                  value={addAttForm.name}
                  onChange={(e) => setAddAttForm((f) => ({ ...f, name: e.target.value.toUpperCase() }))}
                  className="h-11 border border-zinc-300"
                  data-testid="instructor-add-att-name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700">Apellidos *</label>
                <Input
                  value={addAttForm.surname}
                  onChange={(e) => setAddAttForm((f) => ({ ...f, surname: e.target.value.toUpperCase() }))}
                  className="h-11 border border-zinc-300"
                  data-testid="instructor-add-att-surname"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700">DNI *</label>
                <Input
                  value={addAttForm.dni}
                  onChange={(e) => setAddAttForm((f) => ({ ...f, dni: e.target.value.toUpperCase() }))}
                  className="h-11 border border-zinc-300"
                  data-testid="instructor-add-att-dni"
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <label className="text-sm font-semibold text-zinc-700">Profesión</label>
                <Input
                  value={addAttForm.profession}
                  onChange={(e) => setAddAttForm((f) => ({ ...f, profession: e.target.value.toUpperCase() }))}
                  className="h-11 border border-zinc-300"
                  data-testid="instructor-add-att-profession"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddAttOpen(false)}
              className="h-11 border border-zinc-300 hover:bg-zinc-50"
              data-testid="instructor-add-att-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddAttSubmit}
              disabled={addAttSaving || !activeCourse}
              className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="instructor-add-att-submit"
            >
              {addAttSaving ? "Guardando…" : "Añadir + Pedir firma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {askSignatureFor && (
        <SignaturePad
          onCancel={() => setAskSignatureFor(null)}
          onSave={handleSaveStudentSignature}
          saving={false}
          courseName={activeCourse?.name || activeCourse?.client_name || ""}
          studentName={`${askSignatureFor.name} ${askSignatureFor.surname}`.trim()}
        />
      )}

      {/* Captura de firma sobre asistencia ya existente (sesion 1 o 2) */}
      {signTarget && (
        <SignaturePad
          onCancel={() => setSignTarget(null)}
          onSave={handleSaveRowSignature}
          saving={signSaving}
          courseName={
            (activeCourse?.name || activeCourse?.client_name || "") +
            (signTarget.session >= 2 ? ` · ${signTarget.session}ª sesión` : "")
          }
          studentName={`${signTarget.attendance?.name || ""} ${signTarget.attendance?.surname || ""}`.trim()}
        />
      )}

      {/* Confirmar borrado de firma individual */}
      <AlertDialog
        open={!!clearSignatureTarget}
        onOpenChange={(o) => !o && setClearSignatureTarget(null)}
      >
        <AlertDialogContent data-testid="instructor-clear-signature-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar firma?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                Se eliminará la firma{" "}
                {clearSignatureTarget?.session >= 2
                  ? `de la ${clearSignatureTarget.session}ª sesión`
                  : "registrada"}{" "}
                de{" "}
                <span className="font-semibold text-zinc-950">
                  {clearSignatureTarget?.attendance?.name}{" "}
                  {clearSignatureTarget?.attendance?.surname}
                </span>
                . La asistencia se mantiene; sólo se borra esa firma.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="instructor-clear-signature-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearStudentSignature}
              className="bg-red-600 hover:bg-red-700"
              data-testid="instructor-clear-signature-confirm"
            >
              Eliminar firma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Corrector del examen: preguntas con la correcta marcada */}
      <Dialog open={correctorOpen} onOpenChange={setCorrectorOpen}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="corrector-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl flex items-center gap-2">
              <BookOpenCheck className="h-5 w-5" />
              Corrector del examen
            </DialogTitle>
            <DialogDescription>
              Todas las preguntas del examen con la respuesta correcta
              marcada en verde. Úsalo mientras los alumnos están
              respondiendo.
            </DialogDescription>
          </DialogHeader>
          {correctorLoading ? (
            <div className="py-8 text-center text-zinc-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Cargando preguntas…
            </div>
          ) : correctorQuestions.length === 0 ? (
            <div className="py-8 text-center text-zinc-500">
              No hay preguntas cargadas para este curso.
            </div>
          ) : (
            <ol
              className="space-y-4 list-none"
              data-testid="corrector-questions-list"
            >
              {correctorQuestions.map((q, idx) => (
                <li
                  key={q.id}
                  className="border border-zinc-200 rounded-md p-4 bg-white"
                  data-testid={`corrector-q-${q.id}`}
                >
                  <div className="font-semibold text-zinc-900 mb-3">
                    <span className="text-zinc-500 font-mono mr-2">
                      {idx + 1}.
                    </span>
                    {q.text || q.question || "—"}
                  </div>
                  <ul className="space-y-1.5 list-none">
                    {(q.options || []).map((opt, i) => {
                      const isCorrect = i === (q.correct_index ?? q.correct);
                      return (
                        <li
                          key={i}
                          className={`px-3 py-2 rounded border text-sm ${
                            isCorrect
                              ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-semibold"
                              : "bg-zinc-50 border-zinc-200 text-zinc-700"
                          }`}
                        >
                          <span className="font-mono mr-2">
                            {String.fromCharCode(65 + i)}.
                          </span>
                          {opt}
                          {isCorrect && (
                            <CheckCircle2 className="h-4 w-4 inline-block ml-2 -mt-0.5 text-emerald-600" />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ol>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCorrectorOpen(false)}
              data-testid="corrector-close-btn"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
